import { useServices } from '../../app-context';
import { ErrorBanner, HealthIndicator } from '../../components/badges';
import { useAsync } from '../../hooks/use-async';
import { useState } from 'react';

function uptime(seconds: number | null): string {
  if (seconds === null) return '—';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  return days > 0 ? `${days}d ${hours}h` : `${hours}h ${Math.floor((seconds % 3600) / 60)}m`;
}

/** Health (polled every minute by central; refresh polls now) + certificates via the gateway. */
export function HealthSection({ code }: { code: string }) {
  const { api } = useServices();
  const [refresh, setRefresh] = useState(0);
  const report = useAsync(() => api.health(code, refresh > 0), [api, code, refresh]);
  const health = report.data?.health ?? null;

  return (
    <section aria-label="Health">
      <h2>
        Health <HealthIndicator health={health} />
      </h2>
      <ErrorBanner message={report.error} />
      <dl className="facts">
        <dt>Version</dt>
        <dd>{health?.version ?? '—'}</dd>
        <dt>Uptime</dt>
        <dd>{uptime(health?.uptimeSeconds ?? null)}</dd>
        <dt>Database / Keycloak</dt>
        <dd>
          {health?.database ?? '—'} / {health?.keycloak ?? '—'}
        </dd>
        <dt>Users</dt>
        <dd>{health?.userCount ?? '—'}</dd>
        <dt>Last check</dt>
        <dd>
          {health ? `${health.checkedAt}${health.error ? ` — ${health.error}` : ''}` : 'never'}
        </dd>
      </dl>
      <button type="button" onClick={() => setRefresh((r) => r + 1)} disabled={report.loading}>
        Check now
      </button>

      <h3>Hostnames and certificates</h3>
      <table>
        <thead>
          <tr>
            <th>Hostname</th>
            <th>Certificate</th>
            <th>Issuer</th>
            <th>Valid to</th>
          </tr>
        </thead>
        <tbody>
          {(report.data?.certificates ?? []).map((c) => (
            <tr key={c.hostname}>
              <td>{c.hostname}</td>
              <td>{c.ok ? 'valid' : `problem: ${c.error ?? 'unknown'}`}</td>
              <td>{c.issuer ?? '—'}</td>
              <td>{c.validTo ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
